import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { publicFormsApi } from "../lib/api";
import { FieldControl, FieldDisplayBlock, DISPLAY_ONLY_TYPES, headerStyle, fieldVisible } from "../components/marketing/formFields";
import { Loader2, CheckCircle2 } from "lucide-react";

export default function PublicForm() {
  const { formId } = useParams();
  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const f = await publicFormsApi.get(formId);
        setForm(f);
      } catch (e) {
        setError("This form is unavailable.");
      } finally {
        setLoading(false);
      }
    })();
  }, [formId]);

  const accent = form?.accentColor || "#2563EB";
  const onChange = (id, val) => setValues((v) => ({ ...v, [id]: val }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const res = await publicFormsApi.submit(formId, values);
      setDone(res.message || "Thanks! We've received your request.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;

  if (error && !form) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="text-center text-slate-500" data-testid="pf-error">{error}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex items-start sm:items-center justify-center p-4 sm:p-6" data-testid="public-form">
      <div className="w-full max-w-[480px] bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        {(() => { const hs = headerStyle(form.theme, accent); const onAccent = form.theme === "bold"; return (
        <div style={hs.wrap} className="px-6 pt-6 pb-3">
          {form.logoDataUrl ? (
            <img src={form.logoDataUrl} alt={form.companyName} className="h-10 mb-3 object-contain" />
          ) : (
            <div className={`text-[13px] font-semibold mb-2 ${onAccent ? "text-white/80" : "text-slate-500"}`}>{form.companyName}</div>
          )}
          <h1 className={`text-[20px] font-bold ${hs.title}`}>{form.title}</h1>
          {form.intro && <p className={`text-[13.5px] mt-1.5 leading-relaxed ${hs.intro}`}>{form.intro}</p>}
        </div>
        ); })()}

        {done ? (
          <div className="px-6 py-10 flex flex-col items-center text-center" data-testid="pf-success">
            <div className="h-12 w-12 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: `${accent}1a` }}>
              <CheckCircle2 className="h-7 w-7" style={{ color: accent }} />
            </div>
            <p className="text-[14px] text-slate-700 max-w-xs">{done}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 pb-6 pt-4 space-y-4">
            {form.fields.filter((f) => fieldVisible(f, values)).map((f) =>
              DISPLAY_ONLY_TYPES.has(f.type) ? (
                <div key={f.id}><FieldDisplayBlock field={f} accent={accent} /></div>
              ) : (
                <div key={f.id}>
                  {f.type !== "checkbox" && (
                    <label className="block text-[12.5px] font-medium text-slate-700 mb-1.5">
                      {f.label}{f.required && <span className="text-red-500 ml-0.5">*</span>}
                    </label>
                  )}
                  <FieldControl field={f} value={values[f.id]} onChange={(v) => onChange(f.id, v)} accent={accent} inputTextColor={form.inputTextColor} />
                </div>
              )
            )}
            {error && <div className="text-[13px] text-red-600" data-testid="pf-form-error">{error}</div>}
            <button type="submit" disabled={submitting} data-testid="pf-submit"
              style={{ backgroundColor: accent }}
              className="w-full h-11 rounded-lg text-white text-[14px] font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {form.submitLabel}
            </button>
          </form>
        )}
        <div className="px-6 py-3 border-t border-slate-100 text-center">
          <span className="text-[10.5px] text-slate-400">Powered by ServiceGuru Pro</span>
        </div>
      </div>
    </div>
  );
}
